import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../helpers/AuthContext';
import Alert from '../Alert';
import ChannelLookupAutocompleteList from './ChannelLookupAutocompleteList';
import './FriendsManager.css';

const MAX_FRIENDS = 5;

function friendTitle(friend) {
    return friend.channel?.basics?.title || friend.channel?.name || friend.slug;
}

function formatDate(value) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
}

export default function FriendsManager({ onChanged }) {
    const { fetch, API_URL } = useAuth();
    const [friends, setFriends] = useState([]);
    const [friendInput, setFriendInput] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [removingSlug, setRemovingSlug] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const loadFriends = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const response = await fetch(`${API_URL}/v1/seller/friends`, {
                headers: { Accept: 'application/json' },
            });
            const json = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(json.error || 'Failed to load friends');
            setFriends(json.data || []);
        } catch (loadError) {
            setError(loadError.message || 'Failed to load friends');
        } finally {
            setLoading(false);
        }
    }, [API_URL, fetch]);

    useEffect(() => {
        loadFriends();
    }, [loadFriends]);

    async function handleAdd(event) {
        event.preventDefault();
        if (saving || !friendInput.trim()) return;

        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const response = await fetch(`${API_URL}/v1/seller/friends`, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ friend: friendInput }),
            });
            const json = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(json.error || 'Failed to add friend');

            setFriends(json.data || []);
            setFriendInput('');
            setSuccess('Friend added.');
            onChanged?.();
        } catch (addError) {
            setError(addError.message || 'Failed to add friend');
        } finally {
            setSaving(false);
        }
    }

    async function handleRemove(friend) {
        if (!window.confirm(`Remove ${friendTitle(friend)} from your friends?`)) return;

        setRemovingSlug(friend.slug);
        setError('');
        setSuccess('');

        try {
            const response = await fetch(
                `${API_URL}/v1/seller/friends/${encodeURIComponent(friend.slug)}`,
                {
                    method: 'DELETE',
                    headers: { Accept: 'application/json' },
                }
            );
            const json = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(json.error || 'Failed to remove friend');

            setFriends(json.data || []);
            setSuccess('Friend removed.');
            onChanged?.();
        } catch (removeError) {
            setError(removeError.message || 'Failed to remove friend');
        } finally {
            setRemovingSlug(null);
        }
    }

    if (loading) {
        return <p className="help" aria-live="polite">Loading friends...</p>;
    }

    return (
        <div className="fm-manager">
            {success && <Alert type="success">{success}</Alert>}
            {error && <Alert type="error">{error}</Alert>}

            {friends.length === 0 ? (
                <p><em>No friends yet. Add one below!</em></p>
            ) : (
                <div className="fm-friend-list">
                    {friends.map(friend => (
                        <div className="fm-friend-entry" key={friend.slug}>
                            <div className="fm-friend-info">
                                <strong>{friendTitle(friend)}</strong>
                                {friend.channel?.url && (
                                    <a
                                        href={friend.channel.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {friend.channel.url}
                                    </a>
                                )}
                                {formatDate(friend.createdAt) && (
                                    <small>Added on {formatDate(friend.createdAt)}</small>
                                )}
                            </div>
                            <button
                                type="button"
                                className="button danger small"
                                disabled={removingSlug === friend.slug}
                                onClick={() => handleRemove(friend)}
                            >
                                {removingSlug === friend.slug ? 'Removing...' : 'Remove'}
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <form className="fm-add-form" onSubmit={handleAdd}>
                <div className="group">
                    <label htmlFor="fm-friend-input">Add Friend by Store Name or URL</label>
                    <input
                        id="fm-friend-input"
                        type="text"
                        value={friendInput}
                        placeholder="Enter store name or URL"
                        disabled={saving || friends.length >= MAX_FRIENDS}
                        onChange={event => setFriendInput(event.target.value)}
                        aria-autocomplete="list"
                        aria-controls="fm-channel-lookup"
                        required
                    />
                    {!saving && friends.length < MAX_FRIENDS && (
                        <ChannelLookupAutocompleteList
                            id="fm-channel-lookup"
                            query={friendInput}
                            excludedSlugs={friends.map(friend => friend.slug)}
                            onSelect={channel => setFriendInput(channel.slug)}
                        />
                    )}
                    <p className="help">
                        {friends.length} of {MAX_FRIENDS} friends added.
                    </p>
                </div>
                <button
                    type="submit"
                    className="button primary"
                    disabled={saving || friends.length >= MAX_FRIENDS || !friendInput.trim()}
                >
                    {saving ? 'Adding...' : 'Add Friend'}
                </button>
            </form>
        </div>
    );
}
